import React, { useEffect, useState, useCallback } from 'react'
import { useWindowDimensions, Image } from 'react-native'

const fliisiLogo = require('../../assets/fliisi-logo.png')
import { ScreenLayout } from '../components/ScreenLayout'
import { useLanguage } from '@fliisi/i18n'

import {
  MapPin, Heart, User, Search, Store, ChevronRight, ChevronDown, ChevronUp, ShoppingBag,
} from 'lucide-react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import {
  SectionHeader, ProductCard, StoreCard, EmptyState, Text,
  Spinner, IconButton, IconBadge, Card, YStack, XStack, SearchInput,
  FadingScrollView,
} from '@fliisi/pollen'

const STORE_FAVORITES_KEY = 'fliisi_favorite_stores'

interface FeaturedProduct {
  id: string; title: string; price: number; imageUrl: string | null; storeName: string | null; storeId: string | null
}
interface NearbyStore {
  id: string; name: string; storeType: string; city: string | null; productCount: number; isOpen: boolean; imageUrl: string | null; bannerUrl: string | null
}
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'BuyerHome'>
}

export default function BuyerHomeScreen({ navigation }: Props) {
  const { width } = useWindowDimensions()
  const { user } = useAuth()
  const { t } = useLanguage()
  const [products, setProducts] = useState<FeaturedProduct[]>([])
  const [stores, setStores] = useState<NearbyStore[]>([])
  const [loading, setLoading] = useState(true)
  const [favorites, setFavorites] = useState<Set<string>>(new Set())
  const [favoriteIds, setFavoriteIds] = useState<Map<string, string>>(new Map())
  const [favoriteStores, setFavoriteStores] = useState<Set<string>>(new Set())
  const [showAllStores, setShowAllStores] = useState(false)

  useEffect(() => { fetchData(); fetchFavorites(); loadFavoriteStores() }, [user?.id])

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) { setFavorites(new Set()); setFavoriteIds(new Map()); return }
    try {
      const { data } = await supabase.from('favorites').select('id, product_id').eq('user_id', user.id)
      if (data) {
        setFavorites(new Set(data.map(f => f.product_id)))
        setFavoriteIds(new Map(data.map(f => [f.product_id, f.id])))
      }
    } catch {}
  }, [user?.id])

  const toggleFavorite = useCallback(async (productId: string) => {
    if (!user?.id) { navigation.navigate('Login'); return }
    const isFav = favorites.has(productId)
    if (isFav) {
      const favId = favoriteIds.get(productId)
      if (favId) {
        setFavorites(prev => { const n = new Set(prev); n.delete(productId); return n })
        await supabase.from('favorites').delete().eq('id', favId)
      }
    } else {
      setFavorites(prev => new Set(prev).add(productId))
      const { data } = await supabase.from('favorites').insert({ user_id: user.id, product_id: productId }).select('id').single()
      if (data) setFavoriteIds(prev => new Map(prev).set(productId, data.id))
    }
  }, [user?.id, favorites, favoriteIds])

  const loadFavoriteStores = async () => {
    try {
      const stored = await AsyncStorage.getItem(STORE_FAVORITES_KEY)
      if (stored) setFavoriteStores(new Set(JSON.parse(stored)))
    } catch {}
  }

  const toggleStoreFavorite = async (storeId: string) => {
    setFavoriteStores(prev => {
      const n = new Set(prev)
      n.has(storeId) ? n.delete(storeId) : n.add(storeId)
      AsyncStorage.setItem(STORE_FAVORITES_KEY, JSON.stringify([...n]))
      return n
    })
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const query = supabase.from('products')
        .select('id, title, title_fi, title_en, price, flea_market:flea_markets!products_flea_market_id_fkey(id, name), product_images!product_images_product_id_fkey(image_url)')
        .eq('status', 'for_sale').order('created_at', { ascending: false }).limit(12)
      if (user?.id) query.neq('seller_id', user.id)
      const { data } = await query
      if (data) {
        setProducts(data.map((p) => {
          const market = Array.isArray(p.flea_market) ? p.flea_market[0] : p.flea_market
          return { id: p.id, title: p.title_fi || p.title_en || p.title || '', price: p.price || 0, imageUrl: p.product_images?.[0]?.image_url || null, storeName: market?.name || null, storeId: market?.id || null }
        }))
      }
      const { data: storesData } = await supabase.from('flea_markets').select('id, name, store_type, address_city, logo_url, cover_image_url').eq('is_active', true).order('name')
      if (storesData) {
        const withCounts = await Promise.all(storesData.map(async (s) => {
          const { count } = await supabase.from('products').select('id', { count: 'exact', head: true }).eq('flea_market_id', s.id).eq('status', 'for_sale')
          return { id: s.id, name: s.name || '', storeType: s.store_type || 'store', city: s.address_city || null, productCount: count || 0, isOpen: true, imageUrl: s.logo_url || null, bannerUrl: s.cover_image_url || null }
        }))
        setStores(withCounts)
      }
    } catch (e) { console.error('BuyerHome fetch error:', e) }
    finally { setLoading(false) }
  }

  const sortedStores = [...stores].sort((a, b) => {
    const af = favoriteStores.has(a.id) ? 0 : 1; const bf = favoriteStores.has(b.id) ? 0 : 1
    if (af !== bf) return af - bf; return a.name.localeCompare(b.name)
  })
  const COLLAPSED = 5
  const visibleStores = showAllStores ? sortedStores : sortedStores.slice(0, COLLAPSED)

  return (
    <ScreenLayout>
      {/* Header */}
      <XStack jc="space-between" ai="flex-start" mb="$sm">
        <YStack>
          <Image source={fliisiLogo} style={{ width: 120, height: 44 }} resizeMode="contain" />
          <Text style={{ fontFamily: 'PlayfairDisplay_700Bold_Italic', fontSize: 13, color: '#858B80', marginTop: 2, letterSpacing: 0.3 }}>
            Second hand made easy
          </Text>
        </YStack>
        <XStack ai="center" gap={10} mt={4}>
          <IconButton variant="default" size="md" onPress={() => navigation.navigate('Favorites')}>
            <Heart size={20} color="#3E423A" />
          </IconButton>
          <IconButton variant="primary" size="md" onPress={() => navigation.navigate('BuyerProfile')}>
            <User size={20} color="#fff" />
          </IconButton>
        </XStack>
      </XStack>

      {/* Search */}
      <SearchInput
        placeholder={t('search_products_or_stores')}
        editable={false}
        mb="$lg"
        onPress={() => navigation.navigate('SearchScreen')}
      />

      {/* Featured Products */}
      <SectionHeader
        title={t('finds_for_you')}
        actionLabel={t('more')}
        actionIcon={<ChevronRight size={14} color="#748C70" />}
        onAction={() => navigation.navigate('Discoveries')}
        mb="$sm"
      />

      {loading ? (
        <YStack h={200} ai="center" jc="center">
          <Spinner size="large" color="#748C70" />
        </YStack>
      ) : (
        <FadingScrollView fadeWidth={width > ScreenLayout.MaxWidth ? ScreenLayout.Padding : 0} mx={-ScreenLayout.Padding} mb={20}>
          <XStack px={ScreenLayout.Padding} gap={12} pb={8}>
            {products.map(item => (
              <ProductCard
                key={item.id}
                imageUri={item.imageUrl || undefined}
                title={item.title}
                price={`${item.price?.toFixed(2)} €`}
                shopName={item.storeName || undefined}
                shopIcon={<Store size={12} color="#858B80" />}
                isFavorited={favorites.has(item.id)}
                onPress={() => navigation.navigate('ProductView', { productId: item.id })}
                onHeartPress={() => toggleFavorite(item.id)}
              />
            ))}
            {products.length === 0 && (
              <EmptyState title={t('no_products')} icon={<ShoppingBag size={40} color="#858B80" />} />
            )}
          </XStack>
        </FadingScrollView>
      )}

      {/* Nearby Stores */}
      <SectionHeader
        title={t('stores_near_you')}
        actionLabel={t('map_label')}
        onAction={() => navigation.navigate('MapBrowse')}
        mt="$md" mb="$sm"
      />

      <YStack gap={10} mb="$xl">
        {visibleStores.map(shop => (
          <StoreCard
            key={shop.id}
            icon={<Store size={20} color="#748C70" />}
            imageUrl={shop.imageUrl}
            bannerUrl={shop.bannerUrl}
            name={shop.name}
            isOpen={shop.isOpen}
            details={`${shop.city ? shop.city + ' · ' : ''}${shop.productCount} ${t('products')}`}
            right={
              <XStack ai="center" gap="$sm">
                <XStack onPress={() => toggleStoreFavorite(shop.id)} pressStyle={{ opacity: 0.7 }} p="$xs">
                  <Heart size={18} color={favoriteStores.has(shop.id) ? '#E8B49C' : (shop.bannerUrl ? 'rgba(255,255,255,0.6)' : '#ccc')} fill={favoriteStores.has(shop.id) ? '#E8B49C' : 'none'} />
                </XStack>
                <ChevronRight size={16} color={shop.bannerUrl ? 'rgba(255,255,255,0.8)' : '#858B80'} />
              </XStack>
            }
            onPress={() => navigation.navigate('StoreBrowse', { marketId: shop.id, marketName: shop.name })}
          />
        ))}
        {sortedStores.length > COLLAPSED && (
          <XStack
            ai="center" jc="center" gap="$sm" pt="$md"
            onPress={() => setShowAllStores(p => !p)}
            pressStyle={{ opacity: 0.7 }}
            cursor="pointer"
          >
            {showAllStores ? <ChevronUp size={16} color="#748C70" /> : <ChevronDown size={16} color="#748C70" />}
            <Text preset="label" color="$colorPrimary">{showAllStores ? t('show_less') : `${t('view_all')} (${sortedStores.length})`}</Text>
          </XStack>
        )}
      </YStack>

      {/* How it works */}
      <Card variant="flat" bg="rgba(116,140,112,0.09)">
        <Text preset="h3">{t('how_fliisi_works')}</Text>
        <YStack mt="$sm" gap="$sm">
          {[
            { step: '1', text: t('browse_products_online'), Icon: Search },
            { step: '2', text: t('find_nearest_stores'), Icon: MapPin },
            { step: '3', text: t('buy_from_stores'), Icon: ShoppingBag },
          ].map(s => (
            <XStack key={s.step} ai="center" gap="$sm">
              <IconBadge variant="solid" size="sm">
                <Text preset="label" tone="inverse">{s.step}</Text>
              </IconBadge>
              <Text preset="body">{s.text}</Text>
            </XStack>
          ))}
        </YStack>
      </Card>
    </ScreenLayout>
  )
}
